import { Formik } from "formik";
import React, { useEffect, useState } from "react";
import Link from "../../../components/Link";
import useModal from "../../../helpers/useModal";
import { defaultColors, styled } from "../../../stitches.config";
import {
  bodyLarge,
  bodyLargeStrong,
  h3,
  h1
} from "../../commonStyles/Fonts";
import { PaymentStatus } from "../../types/Payment";
import * as Yup from "yup";
import { withThemeContext } from "../../components/StitchesThemeProvider";
import checkIcon from "../../../../src/assets/img/white_check.svg";
import axios from "axios";
import ArrowIcons from "../../../assets/img/left.svg";
import placeholder from "../../../../src/assets/img/natemat/ad_placeholder.png";
import { IArticle } from "../../types/Article";

interface Props {
  onPayButtonClick: (amount: number, email: string, name: string) => void;
  colorMode?: string;
}

const defaultAmounts = [
  {
    amountText: "100 zł",
    amountValue: 100,
  },
  {
    amountText: "50 zł",
    amountValue: 50,
  },
  {
    amountText: "10 zł",
    amountValue: 10,
  },
  {
    amountText: "5 zł",
    amountValue: 5,
  },
  {
    amountText: "Inne",
  },
];

export const SuccessCollectionTileModal = () => {
  return (
    <MessageCollectionTileWrapper>
      <SuccessWrapper>
        <SuccessWrapperHeader className={h1()}>
          Dziękujemy za wpłatę!
        </SuccessWrapperHeader>
        <p className={bodyLarge()}>
          Transakcja przebiegła pomyślnie. Informacje o przebiegu zbiórki będą
          wysyłane na podanego maila. Jako sponsor
          projektu otrzymasz dostęp do materiału wcześniej niż inni!
        </p>
        <SuccessWrapperFooterMsg className={bodyLarge()}>
          Wyczekuj wiadomości!<span>Ekipa NaTemat</span>
        </SuccessWrapperFooterMsg>
      </SuccessWrapper>
    </MessageCollectionTileWrapper>
  );
};

const FailedCollectionTileModal = () => {
  return (
    <MessageCollectionTileWrapper>
      <FailedText className={h3()}>Coś poszło nie tak</FailedText>
    </MessageCollectionTileWrapper>
  );
};

type CollectionTileFromValues = {
  email: string;
  name: string;
  amount: number;
  privacyPolicy: boolean;
};

const validationSchema = Yup.object().shape({
  email: Yup.string()
    .required("Pole wymagane")
    .email("Wprowadź poprawny adres e-mail"),
  amount: Yup.number().required("Pole wymagane").min(1, "Pole wymagane"),
  name: Yup.string().required("Pole wymagane"),
  privacyPolicy: Yup.boolean()
    .required("Musisz zaakceptować warunki")
    .oneOf([true], "Musisz zaakceptować warunki."),
});

const CollectionTileModal: React.FC<Props> = ({ onPayButtonClick, colorMode }) => {
  const { modalData } = useModal();

  const [selectedAmountText, setSelectedAmountText] = useState<string>(
    defaultAmounts[0].amountText
  );
  const [bridDetails, setBridDetails] = useState<IArticle | null>(null);

  useEffect(() => {
    if (modalData?.paymentBridId) {
      const paymentBridId = modalData?.paymentBridId;

      (async () => {
        let article = null;
        try {
          const { data } = await axios.get(`${window.API_HOST}/pwa-article/${paymentBridId}`);
          article = data?.article ?? null;
        } catch { }

        setBridDetails(article);
      })();
    }
  }, [modalData?.paymentBridId]);


  useEffect(() => {
    if (modalData?.paymentValue) {
      // wybór zaznaczonego inputa z kwotą 
      const paymentValue = modalData?.paymentValue;
      const paymentValueNumber = paymentValue === "100" ? "100 zł" : paymentValue === "50" ? "50 zł" : paymentValue === "10" ? "10 zł" : paymentValue === "5" ? "5 zł" : "Inne";
      const valueNumber: number = +paymentValue;
      setSelectedAmountText(paymentValueNumber);
      setInitialValues({
        ...initialValues,
        amount: valueNumber
      });
    }
  }, [modalData?.paymentValue]);

  const [initialValues, setInitialValues] = useState<CollectionTileFromValues>({
    email: "",
    name: "",
    amount: 5,
    privacyPolicy: false,
  });

  const isDarkMode = colorMode === "dark";

  const isOtherValue = selectedAmountText === "Inne";
  switch (modalData?.paymentStatus) {
    case PaymentStatus.SUCCESS:
      if (modalData?.paymentBridId)
        return (
          <Wrapper>
            <SuccessCollectionTileModal />
          </Wrapper>
        );
      break;
    case PaymentStatus.FAILED:
      return (
        <Wrapper>
          <FailedCollectionTileModal />
        </Wrapper>
      );
      break;
  }

  const onSubmit = ({
    email,
    amount,
    name,
    privacyPolicy,
  }: CollectionTileFromValues) => {
    onPayButtonClick(amount, email, name);
  };

  if (!bridDetails)
    return <LoadingText className={h3()}>Ładowanie</LoadingText >;

  return (
    <Wrapper>
      <ImagesWrapper>
        <img
          src={bridDetails.picture.img /*  || placeholder */}
          alt={bridDetails.picture.alt}
        />
      </ImagesWrapper>
      <RightColumn>
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={onSubmit}
        >
          {({
            errors,
            touched,
            handleChange,
            handleBlur,
            values,
            setFieldValue,
            handleSubmit,
          }) => (
            <>
              <h3 className={h3()}>
                {bridDetails.title}
              </h3>
              <form onSubmit={handleSubmit}>
                <p className={bodyLargeStrong()} style={{ marginTop: "16px" }}>
                  Wybierz kwotę:
                </p>
                <AmountWrapper>
                  {defaultAmounts.map(({ amountText, amountValue }, index) => {
                    return (
                      <AmountButton
                        key={index}
                        type="button"
                        isDarkMode={isDarkMode}
                        selected={selectedAmountText === amountText}
                        onClick={() => {
                          setSelectedAmountText(amountText);
                          amountValue && setFieldValue("amount", amountValue);
                        }}
                      >
                        {amountText}
                      </AmountButton>
                    );
                  })}
                </AmountWrapper>
                {isOtherValue && (
                  <>
                    <p className={bodyLargeStrong()}>Podaj kwotę</p>
                    <Input
                      type="number"
                      name="amount"
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.amount}
                      isDarkMode={isDarkMode}
                    />
                  </>
                )}
                {/*<p className={bodySmall()}>Redakcja naTemat finansuje 50% budżetu każdego projektu, który zostanie wybrany przez czytelników. Redakcji zależy na opinii użytkowników i chce współtworzyć portal wspólnie z nimi opierając się na ich opinii oraz interesujących ich tematów.</p>*/}
                <Error>
                  {errors.amount && touched.amount && errors.amount}
                </Error>
                <p className={bodyLargeStrong()}>
                  Imię i nazwisko
                </p>
                <Input
                  name="name"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values.name}
                  isDarkMode={isDarkMode}
                />
                <Error>{errors.name && touched.name && errors.name}</Error>

                <p className={bodyLargeStrong()}>Twój adres e-mail</p>
                <Input
                  name="email"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values.email}
                  isDarkMode={isDarkMode}
                />
                <Error>{errors.email && touched.email && errors.email}</Error>
                <CheckboxWrapper>
                  <Input
                    type="checkbox"
                    className={bodyLarge()}
                    name="privacyPolicy"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    checked={values.privacyPolicy}
                  />
                  <span>
                    Akceptuje&nbsp;
                    <Link to="/info/regulamin">regulamin</Link> i{" "}
                    <Link to="/info/polityka-prywatnosci">
                      politykę prywatności
                    </Link>
                  </span>
                </CheckboxWrapper>
                <Error>
                  {errors.privacyPolicy &&
                    touched.privacyPolicy &&
                    errors.privacyPolicy}
                </Error>
                <PayButton type="submit" className={bodyLarge()}>
                  Przejdź do płatności
                </PayButton>
              </form>
            </>
          )}
        </Formik>
      </RightColumn>
    </Wrapper>
  );
};

export default withThemeContext(CollectionTileModal);

const Wrapper = styled("div", {
  paddingTop: "78px",
  paddingBottom: "31px",
  paddingLeft: "12px",
  paddingRight: "12px",
  img: {
    width: "100%",
    aspectRatio: 1,
    objectFit: "cover",
  },
  "@bp4": {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    paddingTop: "0",
    paddingBottom: "0",
    paddingLeft: "0",
    paddingRight: "0",
    img: {
      width: "705px",
      height: "705px",
      objectFit: "cover",
    },
  },
});

const RightColumn = styled("div", {
  "p:first-of-type": {
    marginTop: "32px",
  },
  "@bp4": {
    "p:first-of-type": {
      marginTop: "0",
    },
    width: "500px",
    padding: "40px",
  },
});

const AmountWrapper = styled("div", {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  "@bp4": {
    gap: "10px",
  },
});

const AmountButton = styled("button", {
  fontSize: 16,
  whiteSpace: "nowrap",
  lineHeight: "24px",
  letterSpacing: "0px",
  fontWeight: "600",
  padding: "10px 10px",
  minWidth: "60px",
  border: "2px solid rgba(0, 0, 0, 0.05)",
  color: "$grayscale0",
  background: "$grayscale100",
  "@bp3": {
    padding: "12px 18px",
  },
  variants: {
    selected: {
      true: {
        border: "2px solid #459B2B !important",
        backgroundColor: "rgba(69, 155, 43, 0.1)",
        position: "relative",
        "&:after": {
          content: `url("${checkIcon}")`,
          position: "absolute",
          width: "16px",
          height: "16px",
          top: "-2px",
          right: "-2px",
          background: "#459B2B",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        },
      },
    },
    isDarkMode: {
      true: {
        borderColor: "rgba(0, 0, 0, 0.05)",
      },
      false: {
        borderColor: "rgba(255, 255, 255, 0.05)",
      },
    },
  },
});


const Input = styled("input", {
  border: "1px solid rgba(0, 0, 0, 0.05)",
  background: "none",
  fontWeight: 400,
  fontSize: "16px",
  padding: "12px 14px",
  color: "$grayscale0",
  width: "100%",
  "&:focus-visible": {
    outline: "1px solid $grayscale10",
  },
  variants: {
    isDarkMode: {
      true: {
        borderColor: "rgba(0, 0, 0, 0.05)",
      },
      false: {
        borderColor: "rgba(255, 255, 255, 0.05)",
      },
    },
  },
});

const CheckboxWrapper = styled("div", {
  display: "flex",
  alignItems: "flex-start",
  marginTop: "16px",
  gap: 10,
  a: {
    color: "$primary",
    "&:hover": {
      color: "$primary",
    }
  },
  input: {
    width: "auto",
    transform: "translateY(30%)",
  },
});

const PayButton = styled("button", {
  background: defaultColors.natematPrimary,
  color: "$grayscale0Fixed",
  textAlign: "center",
  border: "none",
  width: "100%",
  padding: "12px 0",
  marginTop: "16px",
});

const MessageCollectionTileWrapper = styled("div", {
  textAlign: "center",
  //padding: "20px 40px",
  "@bp4": {
    maxWidth: "800px",
  }
});

const Error = styled("span", {
  color: "$aszdziennikPrimary",
  margin: "0 0 10px 0",
  display: "block",
});

const ImagesWrapper = styled("div", {
  "@bp4": {
    display: "flex",
    flexDirection: "row",
    width: "705px",
    height: "705px",
    background: defaultColors.grayscale100,
    justifyContent: "center",
    alignItems: "center",
    margin: "auto",

    "& img": {
      height: "auto",
    },
  },
});

const FailedText = styled("h3", {
  padding: "20px 60px",
  textAlign: "center",
});

// wiadomość sukcecu 

const SuccessWrapper = styled("div", {
  padding: "40px 16px",
  position: "relative",
  background: "$grayscale0",
  color: "$grayscale100",
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
  "&::before, &::after": {
    content: "",
    backgroundImage: `url("${ArrowIcons}")`,
    backgroundRepeat: "no-repeat",
    backgroundSize: "contain",
    display: "block",
    position: "absolute",
    width: "100px",
    height: "200px",
  },
  "&::before": {
    left: "5%",
    top: "-50px",
    transform: "rotate(225deg)",
  },
  "&::after": {
    right: "5%",
    bottom: "-70px",
    transform: "rotate(45deg)",
  },
  "@bp4": {
    marginTop: "70px",
    height: "370px",
    minWidth: "800px",
    "&::before": {
      top: "56%",
      left: "5%",
      transform: "translateY(-50%) rotate(180deg)",
      width: "160px",
      height: "220px",
    },
    "&::after": {
      top: "50%",
      right: "5%",
      transform: "translateY(-50%)",
      width: "160px",
      height: "220px",
    }
  },
  /*
  "@bp5": {
    "&::before": {
      left: "15%",
    },
    "&::after": {
      right: "15%",
    }
  },
  */
  "& p": {
    maxWidth: "540px",
    margin: "0 auto",
    textAlign: "center",
  }
});
const SuccessWrapperHeader = styled("div", {
  textAlign: "center",
  marginBottom: "40px",
  "& span": {
    display: "block",
    color: "$primary",
  },
  "@bp4": {
    marginBottom: "45px",
  }
});
const SuccessWrapperFooterMsg = styled("div", {
  textAlign: "center",
  fontWeight: "800",
  margin: "24px auto !important",
  "@bp4": {
    margin: "32px auto !important"
  },
  "& span": {
    display: "block",
    color: "$primary",
  }
});

const LoadingText = styled("h3", {
  padding: "20px 60px",
  textAlign: "center",
});